import { Box, Typography } from '@mui/material';
import { ReactNode, memo } from 'react';

import { CatalogItemImage } from 'shared/ui/CatalogItemImage';
import { useImageLightbox } from 'shared/ui/ImageLightbox';
import { ProductRowTitleBlock } from 'shared/ui/ProductRowTitleBlock';

export interface CatalogItemProps {
    uom: string;
    brand: string;
    qtyPerUom: number;
    title: string;
    itemLabel: string;
    thumbnailUrl: string;
    isAvailable: boolean;
    isAddedToCatalog?: boolean;
    leadingAction?: ReactNode;
    action: ReactNode;
    supplierSlot: ReactNode;
    priceSlot: ReactNode;
    onClickTitle: () => void;
}

export const CatalogItem = memo(
    ({
        uom,
        brand,
        qtyPerUom,
        title,
        itemLabel,
        thumbnailUrl,
        isAvailable,
        isAddedToCatalog = false,
        leadingAction,
        supplierSlot,
        priceSlot,
        action,
        onClickTitle,
    }: CatalogItemProps) => {
        const { openLightbox } = useImageLightbox();

        return (
            <Box
                sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    position: 'relative',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: '16px',
                    }}
                >
                    {leadingAction ? (
                        <Box
                            sx={{
                                flexShrink: 0,
                                paddingTop: '4px',
                            }}
                        >
                            {leadingAction}
                        </Box>
                    ) : null}
                    <Box
                        onClick={() => openLightbox(thumbnailUrl)}
                        sx={{
                            cursor: 'pointer',
                            position: 'relative',
                            width: '100px',
                            height: '100px',
                            flexShrink: 0,
                            ...(!isAvailable && {
                                backgroundColor: 'white',
                            }),
                            '&:hover': {
                                cursor: 'zoom-in',
                            },
                        }}
                    >
                        <CatalogItemImage
                            src={thumbnailUrl}
                            height="100px"
                            width="100px"
                        />
                        {itemLabel && (
                            <Typography
                                data-testclass="item-inventory-label"
                                typography="openSans.captionMedium"
                                color="black"
                                align="center"
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    backgroundColor:
                                        'rgba(255, 255, 255, 0.85)',
                                    paddingTop: '8px',
                                    paddingBottom: '8px',
                                    fontSize: '12px',
                                }}
                            >
                                {itemLabel}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexGrow: 1,
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            minHeight: '100px',
                            minWidth: 0,
                            gap: '12px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: 0,
                            }}
                        >
                            <ProductRowTitleBlock
                                title={title}
                                brand={brand}
                                uom={uom}
                                qtyPerUom={qtyPerUom}
                                isAddedToCatalog={isAddedToCatalog}
                                interactive
                                onClickTitle={onClickTitle}
                                titleDataTestClass="item-title"
                                brandUomDataTestClass="item-brand-uom"
                            />
                        </Box>

                        <Box sx={{ flexShrink: 0 }}>{supplierSlot}</Box>
                    </Box>

                    <Box
                        sx={{
                            flexShrink: 0,
                            alignSelf: 'stretch',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                            alignItems: 'flex-end',
                        }}
                    >
                        {action}
                    </Box>
                </Box>
                <Box
                    sx={{
                        marginLeft: leadingAction ? '148px' : '120px',
                        paddingLeft: '8px',
                    }}
                >
                    {priceSlot}
                </Box>
            </Box>
        );
    },
);
